import { AppBuilderStreamAPIName } from 'src/constants';
import { buildApiEndpointUrl, getInitForApi } from 'src/utils/AmplifyApiUtils';

import type { MessagePart, SkillsConfig } from './builderSessionApi';

import { ApiMethods, ApiTags, appAPI } from '.';

export type AppBuilderStatus =
  | 'generating'
  | 'ready'
  | 'awaiting_config'
  | 'deploying'
  | 'deployed'
  | 'installed'
  | 'failed';

export interface RequiredSecret {
  key: string;
  label: string;
  description: string;
  type: 'url' | 'api_key' | 'secret' | 'text';
  docsUrl?: string;
}

/**
 * Parsed `.assembly/app.json` surface manifest expressed as paths.
 * Combined with `sandboxUrl` or `deploymentUrl` to construct preview / live
 * URLs. An empty string means the surface was not declared in the manifest;
 * `surfaces` itself being undefined means no manifest has been parsed yet
 * (legacy builders, or builds that haven't completed once).
 */
export interface AppSurfaces {
  internalPath?: string;
  clientPath?: string;
  notificationPath?: string;
}

export interface AppBuilder {
  id: string;
  object: 'appBuilder';
  name: string;
  description: string;
  icon: string;
  status: AppBuilderStatus;
  prompt: string;
  useProductAgent?: boolean;
  deploymentUrl: string;
  sandboxUrl?: string;
  deployErrors?: string;
  installedAppId: string;
  installedModuleId: string;
  modelConfig?: ModelConfig;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  requiredSecrets?: RequiredSecret[];
  surfaces?: AppSurfaces;
  // Internal performance metric anchors (POR-20126). Both nullable —
  // populated lazily on first plan turn / first ready transition, so
  // older AppBuilder rows will have them undefined.
  buildStartedAt?: string;
  firstReadyAt?: string;
}

export interface SkillInfo {
  name: string;
  description: string;
}

export interface PromptInfo {
  filename: string;
  content: string;
}

export interface PromptSlotVersion {
  id: string;
  title: string;
  version: number;
  isDefault: boolean;
  updatedAt: string;
}

export interface PromptSlot {
  slug: string;
  label: string;
  category: string;
  defaultContent: string;
  versions: PromptSlotVersion[] | null;
}

export interface BuildConfigResponse {
  skills: SkillInfo[];
  prompts: {
    planner: PromptInfo;
    builder: PromptInfo;
  };
  slots?: PromptSlot[];
}

export interface UpdateAppBuilderInput {
  id: string;
  name: string;
}

export type AppBuilderMessageActionHint =
  | 'resume'
  | 'send_followup'
  | 'skip'
  | 'allow';

export interface AppBuilderMessage {
  id: string;
  content: string;
  type: string;
  role: string;
  turnId?: string;
  createdAt?: string;
  updatedAt?: string;
  finishReason?: string;
  actionHints?: AppBuilderMessageActionHint[];
  /** Raw message parts from the builder session API. Populated for task_list messages. */
  parts?: MessagePart[];
}

export interface AppBuilderVersionDiff {
  versionId: string;
  versionNumber: number;
  createdAt: string;
  changedFiles: string[];
  addedFiles?: string[];
  removedFiles?: string[];
}

// Product agent types

export interface ProductAgentWizardContext {
  name: string;
  description: string;
  prompt: string;
  icon: string;
}

export interface ProductAgentMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ProductAgentQuestionOption {
  label: string;
  description: string;
}

export interface ProductAgentQuestion {
  question: string;
  header: string;
  options: ProductAgentQuestionOption[];
  multiSelect: boolean;
}

export interface ProductAgentChatInput {
  wizardContext: ProductAgentWizardContext;
  messages: ProductAgentMessage[];
}

export interface ProductAgentResponse {
  message: string;
  questions?: ProductAgentQuestion[];
  isComplete: boolean;
  finalizedPrompt?: string;
}

export class AppBuilderStreamError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AppBuilderStreamError';
    this.status = status;
  }
}

const getStreamErrorMessage = async (response: Response) => {
  try {
    const payload = await response.json();
    if (
      payload &&
      typeof payload === 'object' &&
      'message' in payload &&
      typeof payload.message === 'string'
    ) {
      return payload.message as string;
    }
  } catch (error) {
    // Fall through to the generic message when the response is not JSON.
  }

  return 'Unable to stream message to app builder';
};

export interface PlanTask {
  id: string;
  /** Human-readable headline shown to non-technical reviewers. */
  title: string;
  /** 1–2 sentence non-technical summary of what this task delivers. */
  userSummary: string;
  /** Technical implementation instructions for the build agent (LLM). */
  description: string;
  stepsToVerify: string[];
}

/**
 * In-flight task snapshot sent by the server while a task is still streaming.
 * Any field may be partial ("Set up the dat") or missing entirely. Once the
 * task's closing brace arrives the server emits a full PlanTask via the
 * __planTask__ sentinel and the partial is replaced.
 */
export interface PartialPlanTask {
  idx: number;
  id?: string;
  title?: string;
  userSummary?: string;
  description?: string;
  stepsToVerify?: string[];
}

export const isPartialPlanTask = (value: unknown): value is PartialPlanTask => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.idx === 'number';
};

export const isPlanTask = (value: unknown): value is PlanTask => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.title === 'string' &&
    typeof v.userSummary === 'string' &&
    typeof v.description === 'string' &&
    Array.isArray(v.stepsToVerify) &&
    v.stepsToVerify.every((s) => typeof s === 'string')
  );
};

export const isPlanTaskArray = (value: unknown): value is PlanTask[] =>
  Array.isArray(value) && value.every(isPlanTask);

export interface BuildEvent {
  type:
    | 'task_started'
    | 'task_log'
    | 'task_completed'
    | 'task_failed'
    | 'deploying'
    | 'completed'
    | 'error';
  taskId?: string;
  line?: string;
  url?: string;
  message?: string;
  token?: string;
}

export interface ModelConfig {
  planningModel?: string;
  planEffort?: string;
  buildModel?: string;
  buildEffort?: string;
  noTaskList?: boolean;
}

export interface PrepareBuildOutput {
  appBuilderId: string;
  sandboxName: string;
  repoFullName: string;
  marketplaceAppId: string;
}

export interface StartBuildOutput {
  sessionId: string;
  activeTaskListMessageId: string;
}

export async function prepareBuild(
  appName?: string,
  modelConfig?: ModelConfig,
  prompt?: string,
  useProductAgent?: boolean,
): Promise<PrepareBuildOutput> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({ appName, modelConfig, prompt, useProductAgent }),
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(AppBuilderStreamAPIName, '/app-builders/prepare-build'),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  return response.json() as Promise<PrepareBuildOutput>;
}

export async function startBuild(
  appBuilderId: string,
  sessionId: string,
  skillsConfig?: SkillsConfig,
): Promise<StartBuildOutput> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({
      sessionId,
      ...(skillsConfig ? { skillsConfig } : {}),
    }),
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${appBuilderId}/build`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  return response.json() as Promise<StartBuildOutput>;
}

export const getAppBuilderEntityTag = (id: string) => ({
  type: ApiTags.appBuilders as const,
  id,
});

export const getAppBuilderListTag = () => ({
  type: ApiTags.appBuilders as const,
  id: 'LIST',
});

export const appBuilderApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    listAppBuilders: build.query<AppBuilder[], void>({
      query: () => ({
        path: '/app-builders',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (result) => {
        const itemTags =
          result?.map((item) => getAppBuilderEntityTag(item.id)) ?? [];
        return [getAppBuilderListTag(), ...itemTags];
      },
    }),
    getAppBuilder: build.query<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    deployAppBuilder: build.mutation<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}/deploy`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    submitAppBuilderSecrets: build.mutation<
      AppBuilder,
      { id: string; values: Record<string, string> }
    >({
      query: ({ id, values }) => ({
        path: `/app-builders/${id}/secrets`,
        method: ApiMethods.post,
        options: { body: { values } },
      }),
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    updateAppBuilder: build.mutation<AppBuilder, UpdateAppBuilderInput>({
      query: ({ id, ...body }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.patch,
        options: { body },
      }),
      // A rename updates the linked install's label in module_settings, so the
      // installs (apps) cache that drives sidebar rendering must be refreshed.
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        ApiTags.apps,
      ],
    }),
    deleteAppBuilder: build.mutation<{ success: boolean }, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.del,
        options: {},
      }),
      // The backend's app-builder delete also removes the linked install from
      // module_settings, so invalidate the installs (apps) cache too.
      invalidatesTags: [ApiTags.appBuilders, ApiTags.apps],
    }),
    getBuildConfig: build.query<BuildConfigResponse, void>({
      query: () => ({
        path: '/app-builders/build-config',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [{ type: ApiTags.contextItems, id: 'LIST' }],
    }),
  }),
});

export const {
  useListAppBuildersQuery,
  useGetAppBuilderQuery,
  useDeployAppBuilderMutation,
  useSubmitAppBuilderSecretsMutation,
  useUpdateAppBuilderMutation,
  useDeleteAppBuilderMutation,
  useGetBuildConfigQuery,
} = appBuilderApi;
